import { useState, useEffect } from 'react';
import { apiClient } from '@zaogig/auth';
import { HomeHeader } from '../components/HomeHeader';
import { JobTicker } from '../components/JobTicker';
import { EntryCard } from '../components/EntryCard';
import { TrustStrip } from '../components/TrustStrip';

// ─── Inline types ─────────────────────────────────
interface HomeJobItem {
  id: string;
  title: string;
  timeText: string;
  distanceText: string;
  peopleNeededText: string;
  payText: string;
  tag?: string;
}

interface HomePageProps {
  onLogin?: () => void;
  onJobClick?: (jobId: string) => void;
  onWorkerEntry?: () => void;
  onEnterpriseEntry?: () => void;
}

const mockHomeJobs: HomeJobItem[] = [
  { id: 'job_001', title: '门店促销员', timeText: '今天 14:00-20:00', distanceText: '2.3km', peopleNeededText: '需 4 人', payText: '25/h', tag: '附近' },
  { id: 'job_002', title: '活动签到引导', timeText: '周六 09:00-18:00', distanceText: '3.1km', peopleNeededText: '需 10 人', payText: '220/天', tag: '热门' },
  { id: 'job_003', title: '餐饮晚峰补位', timeText: '今天 18:00-22:00', distanceText: '1.6km', peopleNeededText: '急招 2 人', payText: '30/h', tag: '急招' },
];

const toHomeJobItem = (job: any): HomeJobItem => ({
  id: job.id,
  title: job.title,
  timeText: job.workHours || '待定',
  distanceText: job.distance ? `${job.distance}km` : '未知距离',
  peopleNeededText: job.vacancies ? `需 ${job.vacancies} 人` : '若干人',
  payText: job.salary
    ? `${job.salary.min}/${job.salary.unit === 'hour' ? 'h' : job.salary.unit === 'day' ? '天' : '月'}`
    : '面议',
  tag: job.tags?.[0],
});

export default function HomePage({ onLogin, onJobClick, onWorkerEntry, onEnterpriseEntry }: HomePageProps) {
  const [jobs, setJobs] = useState<HomeJobItem[]>(mockHomeJobs);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadJobs = async () => {
      try {
        const recommendedJobs = await apiClient.get<any[]>('/v1/jobs/recommended', { limit: '5' });
        if (recommendedJobs && recommendedJobs.length > 0) {
          setJobs(recommendedJobs.map(toHomeJobItem));
        }
      } catch (error) {
        console.warn('Failed to load recommended jobs, using mock data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadJobs();
  }, []);

  const handleJobClick = (jobId: string) => {
    sessionStorage.setItem('pendingJobId', jobId);
    onJobClick?.(jobId);
  };

  const handleJobFeedClick = () => {
    onLogin?.();
  };

  const handleWorkerEntry = () => {
    onWorkerEntry?.();
  };

  const handleEnterpriseEntry = () => {
    onEnterpriseEntry?.();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-[-15rem] top-[-15rem] h-[30rem] w-[30rem] rounded-full bg-emerald-200/40 blur-[6rem]" />
        <div className="absolute right-[-12rem] top-1/4 h-[28rem] w-[28rem] rounded-full bg-blue-200/40 blur-[6rem]" />
      </div>

      <HomeHeader onLogin={onLogin} />

      <main className="relative mx-auto flex w-full max-w-7xl flex-col px-4 pb-16 pt-6 sm:px-6 lg:px-8 sm:pt-10">
        <section className="mx-auto max-w-4xl text-center">
          <h1 className="text-4xl font-black tracking-tight text-slate-950 sm:text-5xl lg:text-6xl">
            揾工招人，零工快结
          </h1>
          <p className="mt-4 text-2xl font-bold text-emerald-600 sm:text-3xl lg:text-4xl">
            有早知，话咁易
          </p>
        </section>

        <section className="mt-6 sm:mt-8">
          <JobTicker
            title="今日附近有咩工"
            subtitle="先睇工，再登录"
            jobs={isLoading ? mockHomeJobs : jobs}
            onJobClick={handleJobClick}
            onEmptyClick={handleJobFeedClick}
          />
        </section>

        <section className="mt-6 sm:mt-8 grid gap-4 sm:gap-6 lg:grid-cols-2">
          <EntryCard
            type="worker"
            title="我要揾工"
            description="看附近任务，确认排班，打卡上工，收入清楚。"
            buttonText="微信登录，马上揾工"
            onClick={handleWorkerEntry}
          />

          <EntryCard
            type="enterprise"
            title="我要招人"
            description="发任务，选人，排班，到岗和结算都有记录。"
            buttonText="企业微信登录，马上招人"
            onClick={handleEnterpriseEntry}
          />
        </section>

        <TrustStrip />
      </main>
    </div>
  );
}
